import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Send, Clock, CheckCircle, Truck, CreditCard, Plus, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { parcelsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const SenderDashboard = () => {
  const { user } = useAuth();
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    delivered: 0,
  });

  useEffect(() => {
    fetchParcels();
  }, []);

  const fetchParcels = async () => {
    try {
      const response = await parcelsApi.getAll();
      const data = response.data;
      setParcels(data);
      
      setStats({
        total: data.length,
        pending: data.filter(p => p.status === 'created').length,
        inProgress: data.filter(p => ['accepted', 'in_progress'].includes(p.status)).length,
        delivered: data.filter(p => ['delivered', 'paid'].includes(p.status)).length,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total envois', value: stats.total, icon: Package, color: 'bg-slate-100 text-slate-600' },
    { label: 'En attente', value: stats.pending, icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
    { label: 'En cours', value: stats.inProgress, icon: Truck, color: 'bg-blue-100 text-blue-600' },
    { label: 'Livrés', value: stats.delivered, icon: CheckCircle, color: 'bg-green-100 text-green-600' },
  ];

  const recentParcels = parcels.slice(0, 5);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="sender-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Tableau de bord
          </h1>
          <p className="text-slate-600 mt-1">Gérez vos envois et suivez vos colis</p>
        </div>
        <Link to="/dashboard/new-parcel">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide" data-testid="new-parcel-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nouvel envoi
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-slate-200" data-testid={`stat-card-${index}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/dashboard/new-parcel" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-send">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                <Send className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Envoyer un colis</h3>
                <p className="text-sm text-slate-500">Créer une nouvelle demande</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/parcels" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-parcels">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Mes envois</h3>
                <p className="text-sm text-slate-500">Voir tous vos colis</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/payments" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-payments">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Paiements</h3>
                <p className="text-sm text-slate-500">Historique des transactions</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Parcels */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
              Envois récents
            </CardTitle>
            <Link to="/dashboard/parcels" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
              Voir tout
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </div>
          ) : recentParcels.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun envoi pour le moment</p>
              <Link to="/dashboard/new-parcel">
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600" data-testid="empty-new-parcel-btn">
                  Créer mon premier envoi
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentParcels.map((parcel) => (
                <Link 
                  key={parcel.id} 
                  to={`/dashboard/parcel/${parcel.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`parcel-item-${parcel.id}`}
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {parcel.pickup_address.city} → {parcel.delivery_address.city}
                    </p>
                    <p className="text-sm text-slate-500">
                      {parcel.weight}kg • {formatDate(parcel.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(parcel.status)}`}>
                      {getStatusLabel(parcel.status)}
                    </span>
                    <p className="text-sm font-semibold text-slate-900 mt-1">
                      {formatPrice(parcel.total_price)}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SenderDashboard;
